<template>
  <v-container fluid class="ed-inv">
    <header class="ed-inv__h">
      <span class="ed-inv__h-num">IN</span>
      <h3 class="ed-inv__h-title">Inventario</h3>
      <span class="ed-inv__h-rule"></span>
      <span class="ed-inv__h-meta">Stock, valor y movimientos</span>
    </header>

    <!-- Indicador de Carga General -->
    <v-row v-if="loadingDashboard" class="justify-center">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-col>
    </v-row>

    <!-- Contenido Principal Solo Cuando No Está Cargando el Dashboard -->
    <v-row v-else>
      <!-- Resumen de Estadísticas -->
      <v-col cols="12" md="3" v-for="stat in stats" :key="stat.title">
        <v-card>
          <v-card-text>
            <div class="d-flex justify-space-between align-center">
              <div>
                <p class="text-subtitle-1 text--secondary mb-0">{{ stat.title }}</p>
                <p class="text-h4 font-weight-bold mb-0">
                  {{ stat.value }}
                </p>
              </div>
              <v-icon large :color="stat.color">{{ stat.icon }}</v-icon>
            </div>
            <p class="text-caption text--secondary mt-2">{{ stat.description }}</p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Gráficos -->
    <v-row>
      <!-- Gráfico de Stock por Almacén -->
      <v-col cols="12" md="6" sm="12">
        <v-card class="elevation-3" outlined>
          <v-card-title>Niveles de Stock por Almacén</v-card-title>
          <v-card-subtitle
            >Distribución del stock en diferentes almacenes</v-card-subtitle
          >
          <v-card-text>
            <apexchart
              type="bar"
              height="350"
              :options="stockByWarehouseOptions"
              :series="stockByWarehouseSeries"
            />
            <!-- Estadísticas Adicionales por Almacén -->
            <div class="mt-4">
              <h6>Estadísticas por Almacén:</h6>
              <v-simple-table>
                <thead>
                  <tr>
                    <th>Almacén</th>
                    <th>Stock Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="warehouse in stockByWarehouse.warehouseStats"
                    :key="warehouse.warehouse_id"
                  >
                    <td>{{ warehouse.warehouse_id }}</td>
                    <td>{{ warehouse.total_stock }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Gráfico de Distribución por Categoría -->
      <v-col
        cols="12"
        md="6"
        sm="12"
        v-if="
          categoryDistribution.categories.length && categoryDistribution.series.length
        "
      >
        <v-card class="elevation-3" outlined>
          <v-card-title>Distribución por Categoría</v-card-title>
          <v-card-subtitle>Productos por categoría</v-card-subtitle>
          <v-card-text>
            <apexchart
              type="donut"
              height="350"
              :options="categoryDistributionOptions"
              :series="categoryDistributionSeries"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Gráfico de Tendencias (Solo si hay datos) -->
      <v-col
        cols="12"
        md="6"
        sm="12"
        v-if="stockTrends.categories.length && stockTrends.series.length"
      >
        <v-card class="elevation-3" outlined>
          <v-card-title>Tendencias de Stock</v-card-title>
          <v-card-subtitle>Evolución del stock a lo largo del tiempo</v-card-subtitle>
          <v-card-text>
            <apexchart
              type="line"
              height="350"
              :options="stockTrendsOptions"
              :series="stockTrendsSeries"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Gráfico de Distribución de Stock por Categoría (Solo si hay datos) -->
      <v-col
        cols="12"
        md="6"
        sm="12"
        v-if="stockByCategory.categories.length && stockByCategory.series.length"
      >
        <v-card class="elevation-3" outlined>
          <v-card-title>Distribución de Stock por Categoría</v-card-title>
          <v-card-subtitle>Stock total por categoría</v-card-subtitle>
          <v-card-text>
            <apexchart
              type="bar"
              height="350"
              :options="stockByCategoryOptions"
              :series="stockByCategorySeries"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Gráfico de Comparación Stock vs Límites -->
      <v-col cols="12" md="6" sm="12">
        <v-card class="elevation-3" outlined>
          <v-card-title>Comparación Stock vs Límites</v-card-title>
          <v-card-subtitle>Stock actual vs mínimo y máximo</v-card-subtitle>
          <v-card-text>
            <apexchart
              type="bar"
              height="350"
              :options="stockLimitsOptions"
              :series="stockLimitsSeries"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Gráfico de Valor del Inventario -->
      <v-col cols="12" md="6" sm="12">
        <v-card class="elevation-3" outlined>
          <v-card-title>Valor del Inventario</v-card-title>
          <v-card-subtitle>Valor total por producto</v-card-subtitle>
          <v-card-text>
            <apexchart
              type="bar"
              height="350"
              :options="inventoryValueOptions"
              :series="inventoryValueSeries"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tabla de Items con Bajo Stock -->
    <v-row>
      <v-col cols="12">
        <v-card class="elevation-3" outlined>
          <v-card-title>
            Items con Bajo Stock
            <v-spacer></v-spacer>
            <v-text-field
              v-model="searchLowStock"
              append-icon="mdi-magnify"
              label="Buscar"
              single-line
              hide-details
              @input="debouncedFetchLowStock"
            ></v-text-field>
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="lowStockHeaders"
              :items="lowStockItems"
              :search="searchLowStock"
              :items-per-page="perPageLowStock"
              :page.sync="pageLowStock"
              :server-items-length="totalLowStockItems"
              :loading="loadingLowStock"
              @update:page="fetchLowStock"
              @update:items-per-page="fetchLowStock"
              class="elevation-1"
            >
              <template v-slot:item.price="{ item }">
                {{ formatPrice(item.price) }}
              </template>
              <template v-slot:no-data>
                <v-alert :value="true" color="warning" icon="mdi-alert">
                  No hay datos para mostrar.
                </v-alert>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Indicador de Carga de lowStockItems -->
    <v-row v-if="loadingLowStock && !loadingDashboard" class="justify-center">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import debounce from "lodash/debounce";

export default {
  name: "InventoryDashboard",
  components: {
    apexchart: VueApexCharts,
  },
  data() {
    return {
      // Datos del Dashboard
      stats: [],
      stockByWarehouse: {
        categories: [],
        series: [],
        warehouseStats: [],
      },
      categoryDistribution: {
        categories: [],
        series: [],
      },
      stockLimits: {
        categories: [],
        series: [],
      },
      inventoryValue: {
        categories: [],
        series: [],
      },
      // Nuevos Gráficos
      stockTrends: {
        categories: [],
        series: [],
      },
      stockByCategory: {
        categories: [],
        series: [],
      },
      // Tabla de Items con Bajo Stock
      lowStockItems: [],
      totalLowStockItems: 0,
      lowStockHeaders: [
        { text: "Producto", value: "item_name" },
        { text: "Categoría", value: "item_category_name" },
        { text: "Stock Actual", value: "current_stock" },
        { text: "Stock Mínimo", value: "min_quantity" },
        { text: "Precio", value: "price" },
      ],
      searchLowStock: "",
      pageLowStock: 1,
      perPageLowStock: 10,
      // Indicadores de Carga
      loadingDashboard: false,
      loadingLowStock: false,
      // Debounce
      debouncedFetchLowStock: null,
    };
  },
  methods: {
    formatPrice(value) {
      return `$${Number(value).toFixed(2)}`;
    },

    async fetchDashboard() {
      this.loadingDashboard = true;
      try {
        const response = await this.api_Post("inventory/dashboard", {
          location_id: this.filter.location,
        });

        if (!response.ok) {
          throw new Error(`Error: ${response.statusText}`);
        }

        const data = await response.json();

        // Comprobar que 'stats' existe y es un arreglo
        this.stats = Array.isArray(data.stats)
          ? data.stats.map((stat) => {
              if (stat.title === "Valor Total") {
                return {
                  ...stat,
                  value: this.currencyFormat(stat.value),
                };
              }
              return stat;
            })
          : [];

        // Función para asignar propiedades con comprobaciones
        const assignChartData = (
          target,
          source,
          defaultValue = { categories: [], series: [] }
        ) => {
          target.categories =
            source && source.categories ? source.categories : defaultValue.categories;
          target.series = source && source.series ? source.series : defaultValue.series;
          if (source && source.warehouseStats) {
            target.warehouseStats = source.warehouseStats;
          } else {
            target.warehouseStats = [];
          }
        };

        // Asigna datos a los gráficos, comprobando su existencia
        assignChartData(this.stockByWarehouse, data.stockByWarehouse, {
          categories: [],
          series: [],
          warehouseStats: [],
        });
        assignChartData(this.categoryDistribution, data.categoryDistribution, {
          categories: [],
          series: [],
        });
        assignChartData(this.stockTrends, data.stockTrends, {
          categories: [],
          series: [],
        });
        assignChartData(this.stockByCategory, data.stockByCategory, {
          categories: [],
          series: [],
        });
        assignChartData(this.stockLimits, data.stockLimits, {
          categories: [],
          series: [],
        });
        assignChartData(this.inventoryValue, data.inventoryValue, {
          categories: [],
          series: [],
        });
      } catch (error) {
        // Opcional: mostrar un mensaje de error al usuario
      } finally {
        this.loadingDashboard = false;
      }
    },
    async fetchLowStock() {
      this.loadingLowStock = true;
      try {
        const response = await this.api_Post("inventory/low-stock", {
          location_id: this.filter.location,
          search: this.searchLowStock,
          page: this.pageLowStock,
          perPage: this.perPageLowStock,
        });

        if (!response.ok) {
          throw new Error(`Error: ${response.statusText}`);
        }

        const data = await response.json();

        this.lowStockItems = Array.isArray(data.lowStockItems) ? data.lowStockItems : [];
        this.totalLowStockItems =
          typeof data.totalLowStockItems === "number" ? data.totalLowStockItems : 0;
      } catch (error) {
        console.error("Error fetching low stock data:", error);
        // Opcional: mostrar un mensaje de error al usuario
      } finally {
        this.loadingLowStock = false;
      }
    },
  },
  mounted() {
    // Inicializar Debounce para la Búsqueda en lowStockItems
    // this.debouncedFetchLowStock = debounce(() => {
    //   this.pageLowStock = 1;
    //   this.fetchLowStock();
    // }, 300);
    // // Obtener datos del Dashboard y lowStockItems al montar el componente
    // this.fetchDashboard();
    // this.fetchLowStock();
  },
  props: {
    filter: {
      type: Object,
      required: true,
    },
  },
  watch: {
    "filter.location": {
      handler: function (newVal, oldVal) {
        // Refrescar los datos del Dashboard y lowStockItems cuando cambia el filtro
        this.fetchDashboard();
        this.fetchLowStock();
      },
      immediate: false,
    },
    pageLowStock(newPage) {
      this.fetchLowStock();
    },
    perPageLowStock(newPerPage) {
      this.pageLowStock = 1;
      this.fetchLowStock();
    },
    searchLowStock(newSearch) {
      this.debouncedFetchLowStock();
    },
  },
  computed: {
    stockByWarehouseOptions() {
      return {
        chart: {
          type: "bar",
          stacked: true,
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "55%",
            endingShape: "rounded",
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["transparent"],
        },
        xaxis: {
          categories: this.stockByWarehouse.categories,
          labels: {
            rotate: -45,
            style: {
              fontSize: "12px",
            },
          },
        },
        yaxis: {
          title: {
            text: "Cantidad",
          },
        },
        fill: {
          opacity: 1,
        },
        tooltip: {
          y: {
            formatter: (val) => `${val}`,
          },
        },
        legend: {
          position: "top",
        },
        responsive: [
          {
            breakpoint: 768,
            options: {
              plotOptions: {
                bar: {
                  columnWidth: "70%",
                  distributed: true,
                },
              },
              xaxis: {
                labels: {
                  rotate: -90,
                },
              },
            },
          },
        ],
      };
    },
    categoryDistributionOptions() {
      return {
        chart: {
          type: "donut",
        },
        labels: this.categoryDistribution.categories,
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 250,
              },
              legend: {
                position: "bottom",
              },
            },
          },
        ],
      };
    },
    stockTrendsOptions() {
      return {
        chart: {
          type: "line",
          zoom: {
            enabled: true,
          },
        },
        stroke: {
          curve: "smooth",
        },
        title: {
          text: "Tendencias de Stock",
          align: "left",
        },
        xaxis: {
          categories: this.stockTrends.categories,
          title: {
            text: "Fecha",
          },
        },
        yaxis: {
          title: {
            text: "Cantidad",
          },
        },
        tooltip: {
          y: {
            formatter: (val) => `${val}`,
          },
        },
        legend: {
          position: "top",
        },
      };
    },
    stockByCategoryOptions() {
      return {
        chart: {
          type: "bar",
        },
        plotOptions: {
          bar: {
            horizontal: true,
            distributed: true,
            dataLabels: {
              position: "top",
            },
          },
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val;
          },
          offsetX: 0,
          style: {
            fontSize: "12px",
            colors: ["#304758"],
          },
        },
        xaxis: {
          categories: this.stockByCategory.categories,
          labels: {
            show: false,
          },
        },
        yaxis: {
          title: {
            text: "Categorías",
          },
        },
        tooltip: {
          y: {
            formatter: (val) => `${val}`,
          },
        },
        legend: {
          show: false,
        },
        responsive: [
          {
            breakpoint: 768,
            options: {
              plotOptions: {
                bar: {
                  horizontal: false,
                  distributed: false,
                },
              },
              dataLabels: {
                style: {
                  fontSize: "10px",
                },
              },
              xaxis: {
                labels: {
                  rotate: -45,
                },
              },
            },
          },
        ],
      };
    },
    stockLimitsOptions() {
      return {
        chart: {
          type: "bar",
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "55%",
            endingShape: "rounded",
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["transparent"],
        },
        xaxis: {
          categories: this.stockLimits.categories,
          labels: {
            rotate: -45,
            style: {
              fontSize: "12px",
            },
          },
        },
        yaxis: {
          title: {
            text: "Cantidad",
          },
        },
        fill: {
          opacity: 1,
        },
        tooltip: {
          y: {
            formatter: (val) => `${val}`,
          },
        },
        legend: {
          position: "top",
        },
        responsive: [
          {
            breakpoint: 768,
            options: {
              plotOptions: {
                bar: {
                  columnWidth: "70%",
                  distributed: true,
                },
              },
              xaxis: {
                labels: {
                  rotate: -90,
                },
              },
            },
          },
        ],
      };
    },
    inventoryValueOptions() {
      return {
        chart: {
          type: "bar",
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "55%",
            endingShape: "rounded",
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["transparent"],
        },
        xaxis: {
          categories: this.inventoryValue.categories,
          labels: {
            rotate: -45,
            style: {
              fontSize: "12px",
            },
          },
        },
        yaxis: {
          title: {
            text: "Valor ($)",
          },
        },
        fill: {
          opacity: 1,
        },
        tooltip: {
          y: {
            formatter: (val) => `$${val.toFixed(2)}`,
          },
        },
        legend: {
          position: "top",
        },
        responsive: [
          {
            breakpoint: 768,
            options: {
              plotOptions: {
                bar: {
                  columnWidth: "70%",
                  distributed: true,
                },
              },
              xaxis: {
                labels: {
                  rotate: -90,
                },
              },
            },
          },
        ],
      };
    },
    stockTrendsSeries() {
      return this.stockTrends.series;
    },
    stockByCategorySeries() {
      return this.stockByCategory.series;
    },
    stockByWarehouseSeries() {
      return this.stockByWarehouse.series;
    },
    categoryDistributionSeries() {
      return this.categoryDistribution.series;
    },
    stockLimitsSeries() {
      return this.stockLimits.series;
    },
    inventoryValueSeries() {
      return this.inventoryValue.series;
    },
  },
};
</script>

<style lang="scss" scoped>
$ink:    #0E1B2C;
$graph:  #4B5A6B;
$muted:  #8A98AB;
$rule:   #D9DEE6;
$card:   #FFFFFF;
$brass:  #B68A3E;
$sage:   #4F6F5C;

$display: 'Fraunces', Georgia, serif;
$sans:    'Inter Tight', sans-serif;
$mono:    'JetBrains Mono', monospace;

.ed-inv {
  font-family: $sans; color: $ink;
  padding-top: 8px !important;
}

.ed-inv__h {
  display: flex; align-items: center; gap: 14px;
  margin: 4px 8px 18px;
}
.ed-inv__h-num   { font-family: $mono; font-size: 11px; font-weight: 500; color: $brass; letter-spacing: .14em; }
.ed-inv__h-title { font-family: $display; font-style: italic; font-weight: 400; font-size: 22px; letter-spacing: -.01em; color: $ink; margin: 0; }
.ed-inv__h-rule  { flex: 1; height: 1px; background: $rule; }
.ed-inv__h-meta  { font-family: $sans; font-size: 11px; font-weight: 500; color: $muted; letter-spacing: .14em; text-transform: uppercase; }

::v-deep .v-card {
  height: 100%;
  border-radius: 0 !important;
  border: 1px solid $rule !important;
  box-shadow: none !important;
  background: $card !important;
  transition: background .25s;
}
::v-deep .v-card:hover { background: #FBF9F4 !important; }
::v-deep .v-card__title {
  font-family: $display !important;
  font-size: 19px !important;
  font-weight: 400 !important;
  letter-spacing: -.01em !important;
  color: $ink !important;
  padding-bottom: 4px !important;
}
::v-deep .v-card__subtitle {
  font-family: $sans !important;
  font-size: 11px !important;
  letter-spacing: .14em !important;
  text-transform: uppercase !important;
  color: $muted !important;
}
::v-deep .v-card__text { color: $graph; }
::v-deep .text-h4 {
  font-family: $display !important;
  letter-spacing: -.02em !important;
  color: $ink !important;
}
::v-deep .text-subtitle-1 {
  font-family: $sans !important;
  font-size: 11px !important;
  font-weight: 600 !important;
  letter-spacing: .22em !important;
  text-transform: uppercase !important;
  color: $graph !important;
}
::v-deep .v-data-table {
  font-family: $sans !important;
}
::v-deep .v-data-table thead th {
  font-family: $sans !important;
  font-size: 10px !important;
  letter-spacing: .18em !important;
  text-transform: uppercase !important;
  color: $graph !important;
}
</style>
